/* Admin: edycja stopki — kolumny z linkami (nazwy, kolejność, dodawanie/usuwanie). */
import { Columns3, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { inputCls, labelCls } from "./constants";

const LINK_KIND_OPTIONS = [
  { value: "link", label: "Link (wewnętrzny lub zewnętrzny)" },
  { value: "cookie-settings", label: "Przycisk „Ustawienia cookies\"" },
];

const newLink = () => ({
  id: `fl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  label: "Nowy link",
  href: "/",
  kind: "link",
});

const newColumn = () => ({
  id: `col-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  title: "Nowa kolumna",
  links: [newLink()],
});

export default function FooterSection({ brand, setBrand }) {
  const columns = brand.footer_columns || [];

  const updateColumn = (idx, changes) => {
    const next = columns.map((c, i) => (i === idx ? { ...c, ...changes } : c));
    setBrand({ ...brand, footer_columns: next });
  };

  const removeColumn = (idx) => {
    if (!window.confirm("Usunąć całą kolumnę wraz z linkami?")) return;
    const next = columns.filter((_, i) => i !== idx);
    setBrand({ ...brand, footer_columns: next });
  };

  const moveColumn = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= columns.length) return;
    const next = [...columns];
    [next[idx], next[j]] = [next[j], next[idx]];
    setBrand({ ...brand, footer_columns: next });
  };

  const addColumn = () => {
    setBrand({ ...brand, footer_columns: [...columns, newColumn()] });
  };

  const updateLink = (colIdx, linkIdx, changes) => {
    const col = columns[colIdx];
    const links = col.links.map((l, i) => (i === linkIdx ? { ...l, ...changes } : l));
    updateColumn(colIdx, { links });
  };

  const removeLink = (colIdx, linkIdx) => {
    const col = columns[colIdx];
    const links = col.links.filter((_, i) => i !== linkIdx);
    updateColumn(colIdx, { links });
  };

  const moveLink = (colIdx, linkIdx, dir) => {
    const col = columns[colIdx];
    const links = [...col.links];
    const j = linkIdx + dir;
    if (j < 0 || j >= links.length) return;
    [links[linkIdx], links[j]] = [links[j], links[linkIdx]];
    updateColumn(colIdx, { links });
  };

  const addLink = (colIdx) => {
    const col = columns[colIdx];
    updateColumn(colIdx, { links: [...(col.links || []), newLink()] });
  };

  return (
    <section data-testid="section-footer">
      <h3 className="flex items-center gap-2 font-bold text-slate-800 text-lg mb-4">
        <Columns3 size={18} className="text-[#1f3a8a]" /> Stopka strony
      </h3>
      <p className="text-sm text-slate-600 leading-relaxed mb-4">
        Stopka ma zawsze <b>logo po lewej</b> i <b>Kontakt po prawej</b> (dane pobierane z sekcji „Marka i kontakt").
        Poniżej możesz dodawać, usuwać i przestawiać dowolne kolumny z linkami między nimi.
      </p>

      <div className="space-y-4">
        {columns.length === 0 && (
          <div className="text-sm text-slate-400 py-6 text-center italic border border-dashed border-slate-200 rounded-lg">
            Brak kolumn. Kliknij „Dodaj kolumnę" aby rozpocząć.
          </div>
        )}

        {columns.map((col, ci) => (
          <div
            key={col.id}
            data-testid={`footer-col-${ci}`}
            className="rounded-xl border border-slate-200 p-4 bg-slate-50/50"
          >
            <div className="flex items-start gap-2 mb-3">
              <div className="flex-1">
                <label className={labelCls}>Tytuł kolumny</label>
                <input
                  data-testid={`footer-col-title-${ci}`}
                  className={inputCls}
                  value={col.title}
                  onChange={(e) => updateColumn(ci, { title: e.target.value })}
                  placeholder="np. Oferta, Informacje, Pomoc..."
                />
              </div>
              <div className="flex gap-1 pt-6">
                <button
                  type="button"
                  onClick={() => moveColumn(ci, -1)}
                  disabled={ci === 0}
                  className="p-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-30"
                  title="W lewo"
                >
                  <ArrowUp size={14} className="-rotate-90" />
                </button>
                <button
                  type="button"
                  onClick={() => moveColumn(ci, 1)}
                  disabled={ci === columns.length - 1}
                  className="p-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-30"
                  title="W prawo"
                >
                  <ArrowDown size={14} className="-rotate-90" />
                </button>
                <button
                  data-testid={`footer-col-delete-${ci}`}
                  type="button"
                  onClick={() => removeColumn(ci)}
                  className="p-2 rounded-lg border border-red-200 bg-white text-red-600 hover:bg-red-50"
                  title="Usuń kolumnę"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {(col.links || []).map((link, li) => (
                <div
                  key={link.id}
                  data-testid={`footer-link-row-${ci}-${li}`}
                  className="grid grid-cols-12 gap-2 items-end p-2 bg-white rounded-lg border border-slate-200"
                >
                  <div className="col-span-4">
                    <label className={labelCls}>Nazwa</label>
                    <input
                      data-testid={`footer-link-label-${ci}-${li}`}
                      className={inputCls}
                      value={link.label}
                      onChange={(e) => updateLink(ci, li, { label: e.target.value })}
                    />
                  </div>
                  <div className="col-span-4">
                    <label className={labelCls}>Adres (URL / ścieżka)</label>
                    <input
                      data-testid={`footer-link-href-${ci}-${li}`}
                      className={inputCls}
                      value={link.href}
                      onChange={(e) => updateLink(ci, li, { href: e.target.value })}
                      placeholder={link.kind === "cookie-settings" ? "— (nie wymagane dla ustawień cookies)" : "np. /abonamenty lub https://…"}
                      disabled={link.kind === "cookie-settings"}
                    />
                  </div>
                  <div className="col-span-3">
                    <label className={labelCls}>Typ</label>
                    <select
                      data-testid={`footer-link-kind-${ci}-${li}`}
                      className={inputCls}
                      value={link.kind}
                      onChange={(e) => updateLink(ci, li, { kind: e.target.value })}
                    >
                      {LINK_KIND_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-1 flex justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => moveLink(ci, li, -1)}
                      disabled={li === 0}
                      className="p-1.5 rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-30"
                      title="W górę"
                    >
                      <ArrowUp size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveLink(ci, li, 1)}
                      disabled={li === col.links.length - 1}
                      className="p-1.5 rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-30"
                      title="W dół"
                    >
                      <ArrowDown size={12} />
                    </button>
                    <button
                      data-testid={`footer-link-delete-${ci}-${li}`}
                      type="button"
                      onClick={() => removeLink(ci, li)}
                      className="p-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50"
                      title="Usuń link"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
              <button
                data-testid={`footer-add-link-${ci}`}
                type="button"
                onClick={() => addLink(ci)}
                className="w-full text-xs font-semibold text-[#1f3a8a] hover:text-[#dc2626] py-2 rounded-lg border border-dashed border-slate-300 hover:border-[#dc2626] transition"
              >
                <Plus size={12} className="inline mr-1" /> Dodaj link do „{col.title}"
              </button>
            </div>
          </div>
        ))}

        <button
          data-testid="footer-add-column"
          type="button"
          onClick={addColumn}
          className="w-full py-3 rounded-lg border-2 border-dashed border-slate-300 hover:border-[#dc2626] text-sm font-semibold text-slate-600 hover:text-[#dc2626] transition"
        >
          <Plus size={14} className="inline mr-1" /> Dodaj kolumnę
        </button>
      </div>
    </section>
  );
}
